
import { useState } from 'react';
import { Coins, Trophy, Target, Calendar, TrendingUp, Star, Briefcase } from 'lucide-react';
import MiniAppRoulette from './MiniAppRoulette';

import type { MiniAppView } from './types';

interface BroUser {
  id: string;
  telegram_id: string;
  brocode: string;
  rank: string;
  bro_score: number;
  is_brometheus: boolean;
  twitter_handle?: string;
}

interface MiniAppDashboardProps {
  user: BroUser;
  onNavigate: (view: MiniAppView) => void;
}

const MiniAppDashboard = ({ user, onNavigate }: MiniAppDashboardProps) => {
  const [currentSection, setCurrentSection] = useState<'dashboard' | 'roulette'>('dashboard');


  if (currentSection === 'roulette') {
    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCurrentSection('dashboard')}
            className="text-primary hover:text-foreground"
          >
            ← Back to Dashboard
          </button>
          <h2 className="text-xl font-bold text-foreground">Daily Roulette</h2>
        </div>
        <MiniAppRoulette user={user} />
      </div>
    );
  }


  return (
    <div className="p-4 space-y-6">
      {/* Welcome Header */}
      <div className="text-center bg-secondary rounded-lg p-6 border border-border">
        <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl mx-auto mb-3">
          {user.brocode.slice(0, 2)}
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-1">Welcome, {user.brocode}!</h1>
        <p className="text-muted-foreground mb-2">Rank: {user.rank}</p>
        <div className="flex items-center justify-center gap-2">
          <Coins className="text-primary" size={20} />
          <span className="text-primary font-bold text-lg">{user.bro_score.toLocaleString()} BroScore</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => setCurrentSection('roulette')}
          className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-lg p-4 text-white hover:from-purple-700 hover:to-purple-900 transition-all"
        >
          <div className="text-center">
            <div className="text-2xl mb-2">🎰</div>
            <div className="font-bold">Daily Roulette</div>
            <div className="text-sm opacity-80">Spin for XP!</div>
          </div>
        </button>

      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-secondary rounded-lg p-4 border border-border text-center">
          <Calendar className="text-primary mx-auto mb-2" size={24} />
          <div className="text-foreground font-bold text-lg">0</div>
          <div className="text-muted-foreground text-sm">Day Streak</div>
        </div>
        <div className="bg-secondary rounded-lg p-4 border border-border text-center">
          <TrendingUp className="text-primary mx-auto mb-2" size={24} />
          <div className="text-foreground font-bold text-lg">0</div>
          <div className="text-muted-foreground text-sm">Weekly XP</div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="space-y-3">
        <button
          onClick={() => onNavigate('engagement')}
          className="w-full bg-secondary rounded-lg p-4 border border-border text-left hover:bg-primary hover:bg-opacity-10 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Target className="text-primary" size={20} />
              <div>
                <div className="text-foreground font-bold">Daily Missions</div>
                <div className="text-muted-foreground text-sm">Earn XP through Twitter engagement</div>
              </div>
            </div>
            <span className="text-primary">→</span>
          </div>
        </button>

        <button
          onClick={() => onNavigate('wagers')}
          className="w-full bg-secondary rounded-lg p-4 border border-border text-left hover:bg-primary hover:bg-opacity-10 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Trophy className="text-primary" size={20} />
              <div>
                <div className="text-foreground font-bold">Bro Wagers</div>
                <div className="text-muted-foreground text-sm">Challenge other bros</div>
              </div>
            </div>
            <span className="text-primary">→</span>
          </div>
        </button>

        <button
          onClick={() => onNavigate('jobs')}
          className="w-full bg-secondary rounded-lg p-4 border border-border text-left hover:bg-primary hover:bg-opacity-10 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Briefcase className="text-primary" size={20} />
              <div>
                <div className="text-foreground font-bold">Job Board</div>
                <div className="text-muted-foreground text-sm">Find work or hire talent</div>
              </div>
            </div>
            <span className="text-primary">→</span>
          </div>
        </button>
      </div>

      {/* Achievement Showcase */}
      <div className="bg-secondary rounded-lg p-4 border border-border">
        <h3 className="text-foreground font-bold mb-3 flex items-center gap-2">
          <Star className="text-primary" size={18} />
          Recent Achievement
        </h3>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
            <Trophy className="text-primary-foreground" size={16} />
          </div>
          <div>
            <div className="text-foreground font-bold text-sm">Initiated Bro</div>
            <div className="text-muted-foreground text-xs">Completed the Cipher Challenge</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiniAppDashboard;
